"use client";

import type { CSSProperties, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  Archive,
  ArrowLeft,
  Bath,
  Coffee,
  DoorOpen,
  Monitor,
  Sparkles,
  Star,
  Table2,
  Tv,
} from "lucide-react";

import AddToCartButton from "@/src/features/cart/components/AddToCartButton";
import WishlistToggleButton from "@/src/features/wishlist/components/WishlistToggleButton";

import type { HomeFeaturedProduct } from "../types";

const productCardShellClass =
  "group overflow-hidden rounded-xl border border-[rgba(215,194,165,0.34)] bg-[var(--color-paper-strong)] shadow-[var(--shadow-soft)] md:rounded-[2rem] transition-[box-shadow,border-color] duration-[260ms] hover:border-[rgba(198,169,140,0.38)] hover:shadow-[0_24px_54px_rgba(70,52,39,0.12)]";

const productCardActionsClass =
  "absolute inset-x-0 bottom-0 z-20 hidden gap-3 p-3 opacity-0 pointer-events-none md:flex md:group-hover:pointer-events-auto";

const detailsActionClass =
  "group/details inline-flex h-7 items-center justify-center gap-2 rounded-lg bg-[#b98922] px-3 py-1 text-[10px] font-bold text-white  transition hover:-translate-y-0.5 hover:bg-[#a97919] md:h-auto md:rounded-full md:px-5 md:py-2.5 md:text-sm";

const wishlistActionClass =
  "inline-flex h-7 items-center justify-center gap-2 rounded-lg border border-[rgba(215,194,165,0.42)] bg-white px-3 py-1 text-[10px] font-bold text-[var(--color-ink)]  transition hover:-translate-y-0.5 hover:bg-[var(--color-paper)] hover:shadow-[0_20px_40px_rgba(185, 137, 34, 0.18)] md:h-auto md:rounded-full md:px-5 md:py-2.5 md:text-sm";

const mobileWishlistActionClass =
  "inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[rgba(215,194,165,0.42)] bg-white text-[var(--color-accent-deep)]  transition active:scale-95";

const mobileCartActionClass =
  "inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[rgba(215,194,165,0.42)] bg-white text-[var(--color-accent-deep)]  transition active:scale-95";

const mobileActionIconClass = "h-3.5 w-3.5";

type ProductCardProps = {
  product: HomeFeaturedProduct;
  href?: string;
  mediaHeight?: number;
  mediaAspectRatio?: string;
  className?: string;
  actionLabel?: string;
  highlightQuery?: string;
};

type CategoryIcon = typeof Tv;

const categoryIconsById: Record<number, CategoryIcon> = {
  2: Tv,
  6: Archive,
  7: DoorOpen,
  9: Bath,
  12: Coffee,
  14: Tv,
  15: Table2,
};

function getProductCategoryIcon(product: HomeFeaturedProduct) {
  const directIcon = categoryIconsById[product.categoryId];

  if (directIcon) {
    return directIcon;
  }

  const normalizedCategory = normalizeSearchValue(product.categoryLabel);

  if (normalizedCategory.includes("تليفزيون") || normalizedCategory.includes("tv")) {
    return Tv;
  }

  if (normalizedCategory.includes("تخزين")) {
    return Archive;
  }

  if (normalizedCategory.includes("جزام")) {
    return DoorOpen;
  }

  if (normalizedCategory.includes("حمام")) {
    return Bath;
  }

  if (normalizedCategory.includes("كوفي") || normalizedCategory.includes("coffee")) {
    return Coffee;
  }

  if (normalizedCategory.includes("ترابيز") || normalizedCategory.includes("table")) {
    return Table2;
  }

  return Monitor;
}

function formatRating(value: number | null) {
  if (value === null) {
    return null;
  }

  return value.toFixed(1);
}

function getMobileProductTitle(title: string) {
  return title.trim().split(/\s+/).slice(0, 3).join(" ");
}

const arabicDiacriticsPattern = /[\u064b-\u065f\u0670]/g;

function normalizeSearchValue(value: string) {
  return value
    .toLowerCase()
    .replace(arabicDiacriticsPattern, "")
    .replace(/\u0640/g, "")
    .replace(/[أإآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/\s+/g, " ")
    .trim();
}

function buildNormalizedIndex(value: string) {
  let normalized = "";
  const originalIndexes: number[] = [];

  Array.from(value).forEach((char, index) => {
    const nextChar = normalizeSearchValue(char);

    if (!nextChar) {
      return;
    }

    Array.from(nextChar).forEach((normalizedChar) => {
      normalized += normalizedChar;
      originalIndexes.push(index);
    });
  });

  return { normalized, originalIndexes };
}

function highlightSearchMatch(text: string, query?: string): ReactNode {
  const normalizedQuery = normalizeSearchValue(query ?? "");

  if (!normalizedQuery) {
    return text;
  }

  const { normalized, originalIndexes } = buildNormalizedIndex(text);
  const matchIndex = normalized.indexOf(normalizedQuery);

  if (matchIndex === -1) {
    return text;
  }

  const start = originalIndexes[matchIndex];
  const end =
    (originalIndexes[matchIndex + normalizedQuery.length - 1] ?? start) + 1;

  return (
    <>
      {text.slice(0, start)}
      <mark className="rounded-md bg-[#f3cf78]/70 px-1 text-[var(--color-ink)] ring-1 ring-[#b98922]/30">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

export default function ProductCard({
  product,
  href = product.variantId
    ? `/products/variant/${product.variantId}`
    : `/products/${product.slug}`,
  mediaHeight = 144,
  mediaAspectRatio,
  className = "",
  actionLabel = "اعرف تفاصيل القطعة",
  highlightQuery = "",
}: ProductCardProps) {
  const hoverImage =
    product.gallery.find((galleryImage) => galleryImage !== product.image) ??
    product.hoverImage;
  const hasHoverImage = hoverImage !== product.image;
  const roundedRating = Math.round(product.rating ?? 0);
  const formattedRating = formatRating(product.rating);
  const CategoryIcon = getProductCategoryIcon(product);
  const mobileTitle = getMobileProductTitle(product.title);
  const highlightedTitle = highlightSearchMatch(product.title, highlightQuery);
  const highlightedMobileTitle = highlightSearchMatch(
    mobileTitle,
    highlightQuery,
  );
  const prefersReducedMotion = useReducedMotion();
  const hoverProgress = useMotionValue(0);
  const smoothHoverProgress = useSpring(hoverProgress, {
    damping: 24,
    mass: 0.18,
    stiffness: 220,
  });
  const hoverDriver = prefersReducedMotion
    ? hoverProgress
    : smoothHoverProgress;
  const imageLift = useTransform(hoverDriver, [0, 1], [0, 0]);
  const imageScale = useTransform(hoverDriver, [0, 1], [1, 1]);
  const actionsY = useTransform(hoverDriver, [0, 1], [18, 0]);
  const actionsOpacity = useTransform(hoverDriver, [0, 1], [0, 1]);
  const mediaStyle = mediaAspectRatio
    ? ({ aspectRatio: mediaAspectRatio } as CSSProperties)
    : ({ "--media-height": `${mediaHeight}px` } as CSSProperties);
  const mediaClassName = mediaAspectRatio
    ? "relative h-auto overflow-hidden rounded-t-xl bg-transparent md:rounded-t-[2rem]"
    : "relative h-36 overflow-hidden rounded-t-xl bg-transparent md:h-[var(--media-height)] md:rounded-t-[2rem]";

  const handlePointerEnter = () => {
    hoverProgress.set(1);
  };

  const handlePointerLeave = () => {
    hoverProgress.set(0);
  };

  return (
    <div className="h-full">
      <motion.article
        className={`${productCardShellClass} flex h-full flex-col ${className}`.trim()}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      >
        <div className={mediaClassName} style={mediaStyle}>
          <Link
            href={href}
            aria-label={`عرض تفاصيل ${product.title}`}
            className="absolute inset-0 z-0 block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b98922] focus-visible:ring-offset-2"
          >
            <motion.div
              className="absolute inset-0"
              style={{
                scale: imageScale,
                y: imageLift,
                willChange: "transform, opacity",
              }}
            >
              <Image
                src={product.image}
                alt=""
                fill
                sizes="(min-width: 1280px) 28vw, (min-width: 768px) 45vw, 100vw"
                loading="lazy"
                className={`object-contain object-top transition-opacity duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                  hasHoverImage ? "group-hover:opacity-0" : ""
                }`}
              />
            </motion.div>
            {hasHoverImage ? (
              <Image
                src={hoverImage}
                alt=""
                fill
                sizes="(min-width: 1280px) 28vw, (min-width: 768px) 45vw, 100vw"
                loading="lazy"
                className="object-contain object-top opacity-0 transition-opacity duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:opacity-100"
              />
            ) : null}
          </Link>

          <div className="absolute inset-x-2 top-2 z-20 flex items-center gap-2 md:hidden">
            <WishlistToggleButton
              variantId={product.variantId}
              title={product.title}
              className={mobileWishlistActionClass}
              filledClassName="border-[#b98922] bg-white !text-[#b98922] shadow-[0_16px_30px_rgba(185, 137, 34, 0.22)]"
              iconClassName={mobileActionIconClass}
              hideIcon={false}
            />
            <AddToCartButton
              variantId={product.variantId}
              isAvailable={product.isAvailable || product.stock > 0}
              iconOnly
              ariaLabel={`أضف ${product.title} إلى السلة`}
              className={mobileCartActionClass}
              iconClassName={mobileActionIconClass}
            />
          </div>

          {product.isNew ? (
            <div className="pointer-events-none absolute left-2 top-12 z-10 opacity-100 transition-opacity duration-300 md:left-5 md:top-16 md:opacity-0 md:group-hover:opacity-100">
              <p className="inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/90 px-2 py-1 text-[10px] font-semibold text-white shadow-lg shadow-emerald-700/20 backdrop-blur-sm md:px-3 md:py-1.5 md:text-xs">
                <Sparkles className="h-3 w-3 md:h-3.5 md:w-3.5" />
                جديد
              </p>
            </div>
          ) : null}

          <motion.div
            className={productCardActionsClass}
            style={{
              opacity: actionsOpacity,
              y: actionsY,
              willChange: "transform, opacity",
            }}
          >
            <WishlistToggleButton
              variantId={product.variantId}
              title={product.title}
              className={`${wishlistActionClass} flex-1`}
              filledClassName="border-[rgba(215,194,165,0.42)] bg-white text-[var(--color-ink)] shadow-[0_14px_30px_rgba(185, 137, 34, 0.14)]"
              label="أضف للمفضلة"
              activeLabel="تمت الإضافة"
              hideIcon
            />
            <AddToCartButton
              variantId={product.variantId}
              isAvailable={product.isAvailable || product.stock > 0}
              label="أضف للسلة"
              activeLabel="تمت الإضافة"
              className="h-10 min-w-[9rem] flex-none rounded-full border-transparent bg-[#b98922] px-4 py-2 text-sm text-white shadow-[0_18px_40px_rgba(185, 137, 34, 0.24)] hover:bg-[#b98922] hover:text-white"
            />
          </motion.div>
        </div>

        <div className="flex flex-1 flex-col bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(250,247,240,0.95))] p-2 md:px-6">
          <div className="flex items-center justify-start">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-[#b98922]/45 bg-white/70 px-2.5 py-1 text-[9px] font-bold text-[#b98922] shadow-[0_10px_24px_rgba(70,52,39,0.06)] md:px-4 md:py-2 md:text-sm">
              <CategoryIcon className="h-3 w-3 md:h-4 md:w-4" />
              {product.categoryLabel}
            </p>
          </div>

          <h3 className="mt-2 line-clamp-2 text-[12px] font-semibold leading-tight text-[var(--color-ink)] md:mt-4 md:line-clamp-1 md:text-2xl">
            <span className="md:hidden">{highlightedMobileTitle}</span>
            <span className="hidden md:inline">{highlightedTitle}</span>
          </h3>
          <p className="mt-1 line-clamp-1 text-[10px] leading-4 text-[var(--color-ink-soft)] md:mt-3 md:text-base md:leading-7">
            {product.shortDescription}
          </p>

          <div className="mt-2 flex flex-col gap-2 py-2 md:mt-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between md:gap-3">
              {/* Price */}
              <div className="w-full min-w-0 text-end md:w-auto">
                {product.originalPrice !== product.price && (
                  <div
                    dir="ltr"
                    className="mb-1 flex max-w-full items-baseline justify-end gap-1 text-[10px] text-[var(--color-ink-muted)] sm:text-xs md:text-sm"
                  >
                    <span dir="rtl" className="text-[0.85em]">
                      ج.م
                    </span>
                    <span className="tabular-nums line-through">
                      {product.originalPrice}
                    </span>
                  </div>
                )}

                <div
                  dir="ltr"
                  className="flex max-w-full items-baseline justify-end gap-1.5 text-[#b98922]"
                >
                  <span
                    dir="rtl"
                    className="text-xs font-semibold sm:text-sm md:text-base"
                  >
                    ج.م
                  </span>
                  <span className="max-w-full tabular-nums text-lg font-bold leading-none sm:text-xl md:text-2xl">
                    {product.price}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 md:block">
                <Link
                  href={href}
                  className={`min-w-0 flex-1 pt-1.5 md:hidden ${detailsActionClass}`}
                >
                  <span className="truncate">اعرف اكثر</span>
                  <ArrowLeft className="h-3 w-3 transition-transform duration-300 group-hover/details:-translate-x-1" />

                </Link>

                {/* Rating */}
                <div
                  dir="ltr"
                  className="flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-[#d7c2a5]/50 bg-white/60 px-2.5 shadow-[0_8px_24px_rgba(70,52,39,0.06)] md:h-auto md:gap-2 md:rounded-2xl md:px-4 md:py-2.5"
                >
                  {formattedRating && (
                    <span className="tabular-nums text-xs font-bold leading-none text-[var(--color-ink)] md:text-base">
                      {formattedRating}
                    </span>
                  )}

                  <Star
                    strokeWidth={1.8}
                    className="size-3 fill-current text-[var(--color-gold)] md:hidden"
                  />

                  <div className="hidden items-center gap-1 text-[var(--color-gold)] md:flex">
                    {Array.from({ length: 5 }).map((_, starIndex) => (
                      <Star
                        key={starIndex}
                        strokeWidth={1.8}
                        className={`size-4 ${
                          starIndex < roundedRating ? "fill-current" : ""
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-auto hidden md:block">
            <Link
              href={href}
              className={`${detailsActionClass} w-full pt-1.5 md:mb-3 md:inline-flex`}
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover/details:-translate-x-1" />
              {actionLabel}
            </Link>
          </div>
        </div>
      </motion.article>
    </div>
  );
}
